import axios from "axios";

const tenantId = window.location.hostname.split(".")[0];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL.replace(
  "{tenant_id}",
  tenantId
);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Estimate subscription cost including taxes
export const estimateSubscription = async (billingConnectionId, subscriptionData, options = {}) => {
  const response = await api.post(
    `/api/checkout/v1/${billingConnectionId}/estimate-subscription`,
    subscriptionData,
    options.signal ? { signal: options.signal } : undefined
  );
  return response.data;
};

/**
 * Polling fallback for the payment outcome.
 *
 * The backend WebSocket push only reaches the instance that happens to hold the socket, so
 * with more than one backend instance a status update is dropped whenever the provider
 * webhook is consumed elsewhere. Polling this turns that miss into a short delay rather than
 * a checkout that spins forever. Intentionally lean (status + updatedAt) because it is polled.
 */
export const fetchTransactionStatus = async (transactionId) => {
  const response = await api.get(
    `/api/checkout/v1/transactions/${transactionId}/status`
  );
  return response.data;
};

/** In-flight / resolved gatewayConnection GETs keyed by routing id. */
const gatewayConnectionByRoutingId = new Map();

/**
 * Fetch gateway connection for a routing entry.
 * Concurrent callers (selector prefetch + card form mount) share one request
 * so Altruon embedded checkout does not hit the API twice.
 */
export const fetchGatewayConnection = async (routing, t) => {
  const routingId = routing?.id;
  if (!routingId) {
    throw new Error(
      (typeof t === "function" && t("messages.error.gatewayUnavailable")) ||
        "Gateway unavailable"
    );
  }

  const cached = gatewayConnectionByRoutingId.get(routingId);
  if (cached) return cached;

  const request = api
    .get(`/api/checkout/v1/${routingId}/gatewayConnection`)
    .then((response) => {
      if (response.status === 200) return response.data;
      throw new Error(
        (typeof t === "function" && t("messages.error.gatewayUnavailable")) ||
          "Gateway unavailable"
      );
    })
    .catch((error) => {
      gatewayConnectionByRoutingId.delete(routingId);
      throw error;
    });

  gatewayConnectionByRoutingId.set(routingId, request);
  return request;
};


/**
 * Endpoint the card forms submit to. Defaults to the classic checkout charge;
 * the add-payment-source hosted page switches it to the zero-amount SETUP
 * endpoint so every gateway form can be reused unchanged.
 */
let processPaymentEndpoint = "process-payment";

export const setProcessPaymentEndpointMode = (mode) => {
  processPaymentEndpoint =
    mode === "setup" ? "process-setup" : "process-payment";
};

export const processPayment = async (
  billingPlatformId,
  sessionId,
  payload
) => {
  return api.post(
    `/api/checkout/v1/${billingPlatformId}/${processPaymentEndpoint}/${sessionId}`,
    payload
  );
};

/** Context for the add-payment-source hosted page (/checkout/a/{sessionId}). */
export const fetchAddPaymentSourceContext = async (sessionId) => {
  const response = await api.get(
    `/api/checkout/v1/add-payment-source/${sessionId}`
  );
  return response.data;
};

/** Verifies the setup outcome after a 3DS/redirect return and completes the flow. */
export const finalizeAddPaymentSource = async (sessionId, transactionId) => {
  const response = await api.post(
    `/api/checkout/v1/add-payment-source/${sessionId}/finalize/${transactionId}`
  );
  return response.data;
};

/** Completes Adyen 3DS / additional-details after handleAction in the card component. */
export const submitAdyenPaymentDetails = async ({ details, paymentData, transactionId }) => {
  return api.post(`/api/checkout/v1/adyen/payment-details`, {
    details,
    paymentData,
    transactionId,
  });
};

/**
 * Records a gateway SDK decline (e.g. Nuvei createPayment refusal) before process-payment.
 */
export const recordCitGatewayDecline = async (
  billingPlatformId,
  sessionId,
  { checkoutRequest, failureMessage, statusDetail, statusCode, idAtPaymentGateway, gatewayResponse }
) => {
  return api.post(
    `/api/checkout/v1/${billingPlatformId}/record-gateway-decline/${sessionId}`,
    {
      checkoutRequest,
      failureMessage,
      statusDetail,
      statusCode,
      idAtPaymentGateway,
      gatewayResponse,
    }
  );
};


/** In-flight / resolved idempotent GETs keyed by full request path (+ query). */
const idempotentGetCache = new Map();

/**
 * Share identical GETs across MetadataBootstrap, useCheckoutPage, card forms, etc.
 * Failed requests are evicted so callers can retry.
 */
export const idempotentGet = (url, config) => {
  const existing = idempotentGetCache.get(url);
  if (existing) return existing;

  const request = api.get(url, config).catch((error) => {
    idempotentGetCache.delete(url);
    throw error;
  });
  idempotentGetCache.set(url, request);
  return request;
};

export const invalidateIdempotentGet = (urlOrPrefix) => {
  for (const key of [...idempotentGetCache.keys()]) {
    if (key === urlOrPrefix || key.startsWith(urlOrPrefix)) {
      idempotentGetCache.delete(key);
    }
  }
};

/**
 * Catalog payload for a billing platform (+ optional plan / session).
 * Returns response.data (itemPrices, routingEntries, checkoutPageConfDto, …).
 */
export const fetchCheckoutDetails = async (
  billingPlatformId,
  planId,
  sessionId
) => {
  if (!billingPlatformId) return null;
  const params = new URLSearchParams();
  if (planId) params.append("planId", planId);
  if (sessionId) params.append("sessionId", sessionId);
  const qs = params.toString();
  const url = `/api/checkout/v1/${billingPlatformId}${qs ? `?${qs}` : ""}`;
  const response = await idempotentGet(url);
  return response.data;
};

/** Tenant branding / checkout conf resolved from the domain alone. */
export const fetchCheckoutConf = async () => {
  const response = await idempotentGet(`/api/checkout/v1/conf`);
  return response.data;
};

export const fetchtLargeUrl = async (shortKey) => {
  try {
    const response = await api.get(`/api/checkout/v1/resolve/${shortKey}`, {
      shortKey,
    });
    return response.data;
  } catch (err) {
    console.error("Error creating short URL:", err);
    throw err;
  }
};


export const resolveCheckoutToken = async (token) => {
  try {
    const response = await api.get(`/api/checkout/v1/resolve?token=${token}`);
    return response.data;
  } catch (err) {
    console.error("Error creating short URL:", err);
    throw err;
  }
};

/**
 * Resolve an opaque hosted page link. Returns server-authoritative display data
 * (billing platform, plan, currency, line items, coupons) for the checkout to
 * render before creating a session.
 */
export const resumeHostedCheckoutSession = async (configId, trxId) => {
  const response = await api.get(`/api/checkout/v1/hosted-pages/${configId}/resume`, {
    params: { trx_id: trxId },
  });
  return response?.data;
};

export const resumeDynamicHostedCheckoutSession = async (sessionId, trxId) => {
  const response = await api.get(`/api/checkout/v1/sessions/${sessionId}/resume`, {
    params: { trx_id: trxId },
  });
  return response?.data;
};

export const resolveHostedPage = async (configId) => {
  const response = await api.get(`/api/checkout/v1/hosted-pages/${configId}`);
  return response.data;
};

/**
 * Mint a checkout session for a hosted page link (/checkout/p/{configId}).
 * Cart and pricing are resolved server-side from the stored config.
 */
export const startHostedPageCheckout = async (configId) => {
  const response = await api.post(`/api/checkout/v1/hosted-pages/${configId}/start`);
  return response.data;
};

/**
 * Resolve a Checkout.com payment-session amount in minor units (integer cents).
 * Prefers the billing estimate total, then plan + addon line items.
 */
export const resolveCheckoutAmountMinor = ({
  amount,
  itemDetails,
  platformItemDetails,
  selectedAddons,
  estimatedOrder,
}) => {
  if (typeof amount === "number" && amount > 0) {
    return Math.round(amount);
  }

  const estimatedMajor = Number(estimatedOrder?.futureInvoiceAmountDue);
  if (estimatedMajor > 0) {
    return Math.round(estimatedMajor * 100);
  }

  const major = resolveNuveiAmount({
    itemDetails,
    platformItemDetails,
    selectedAddons,
  });
  if (major != null && major > 0) {
    return Math.round(major * 100);
  }

  const fallback = Number(itemDetails?.price);
  if (fallback > 0) {
    return Math.round(fallback * 100);
  }
  // Zero-amount SETUP flows (add-payment-source hosted page) must still send 0 so
  // Checkout.com can create a tokenization payment session — omitting amount 500s.
  if (fallback === 0) {
    return 0;
  }

  return null;
};

/**
 * Asks the backend to authorize a Basis Theory Elements session so the card form can create a
 * token intent. A 409 means Altruon vaulting is not on for this checkout.
 */
export const authorizeVaultSession = async (sessionId, nonce) => {
  const { data } = await api.post(`/api/checkout/v1/${sessionId}/vault/session`, { nonce });
  return data;
};

export const createCheckoutComPaymentSession = async ({
  sessionId,
  itemDetails,
  formData,
  clientCountry,
  platformItemDetails,
  selectedAddons,
  estimatedOrder,
  amount,
}) => {
  const resolvedAmount = resolveCheckoutAmountMinor({
    amount,
    itemDetails,
    platformItemDetails,
    selectedAddons,
    estimatedOrder,
  });

  const { data } = await api.post(
    `/api/checkout/v1/${sessionId}/checkout-com/payment-session`,
    {
      ...(resolvedAmount != null && { amount: resolvedAmount }),
      currency: itemDetails?.currencyCode || "USD",
      customer: {
        name:
          formData?.firstName && formData?.lastName
            ? `${formData.firstName} ${formData.lastName}`
            : "Customer",
        email: formData?.email || "",
      },
      billing: {
        address: {
          country: formData?.billingAddress?.country || clientCountry || "US",
        },
      },
    }
  );

  return data;
};

export const createCheckoutComApplePaySession = async ({
  sessionId,
  itemDetails,
  formData,
  clientCountry,
  platformItemDetails,
  selectedAddons,
  estimatedOrder,
  amount,
}) => {
  const resolvedAmount = resolveCheckoutAmountMinor({
    amount,
    itemDetails,
    platformItemDetails,
    selectedAddons,
    estimatedOrder,
  });

  const { data } = await api.post(
    `/api/checkout/v1/${sessionId}/checkout-com/payment-session`,
    {
      ...(resolvedAmount != null && { amount: resolvedAmount }),
      currency: itemDetails?.currencyCode || "USD",
      payment_type: "Recurring",
      customer: {
        name:
          formData?.firstName && formData?.lastName
            ? `${formData.firstName} ${formData.lastName}`
            : "",
        email: formData?.email || "",
      },
      billing: {
        address: {
          country: formData?.billingAddress?.country || clientCountry || "US",
        },
      },
    }
  );

  return data;
};

/**
 * Compute the Nuvei openOrder amount in major units from the hosted-page state.
 *
 * Nuvei rejects createPayment with errCode 1201 ("Invalid Amount") if the
 * session was opened with 0.00 or a mismatched value, so we pick the best
 * non-zero source we have:
 *
 *   1. Explicit `amount` override from the caller (e.g. latest estimated total)
 *   2. Sum of `platformItemDetails` (plan + addons), which comes from the
 *      backend and is always in minor units
 *   3. `itemDetails.price` from the URL params (treated as minor units)
 *   4. `null` (let the backend fall back to the session's plan price)
 */
const resolveNuveiAmount = ({ amount, itemDetails, platformItemDetails, selectedAddons }) => {
  if (typeof amount === "number" && amount > 0) return amount;

  if (Array.isArray(platformItemDetails) && platformItemDetails.length > 0) {
    // Plan is always the first entry; remaining entries may be selected addons.
    const plan = platformItemDetails[0];
    const planMinor = Number(plan?.price) || 0;

    const addonMinor = Array.isArray(selectedAddons)
      ? selectedAddons.reduce((acc, addon) => {
          const unit =
            Number(addon?.unitPrice ?? addon?.price ?? 0) || 0;
          const qty  = Number(addon?.quantity) || 0;
          return acc + unit * qty;
        }, 0)
      : 0;

    const totalMinor = planMinor + addonMinor;
    if (totalMinor > 0) return totalMinor / 100;
  }

  const fallback = Number(itemDetails?.price);
  if (fallback > 0) return fallback / 100;

  return null;
};

export const createNuveiOpenOrder = async ({
  sessionId,
  itemDetails,
  formData,
  platformItemDetails,
  selectedAddons,
  amount,
}) => {
  const billing = formData?.billingAddress || {};

  // Only include keys that have non-empty values so Nuvei can fall back
  // gracefully for any field not yet collected.
  const customer = {
    ...(formData?.firstName && { firstName: formData.firstName }),
    ...(formData?.lastName  && { lastName:  formData.lastName }),
    ...(formData?.email     && { email:     formData.email }),
    ...(formData?.phone     && { phone:     formData.phone }),
  };

  const billingAddress = {
    ...(billing.country      && { country:   billing.country }),
    ...(billing.city         && { city:      billing.city }),
    ...(billing.state        && { state:     billing.state }),
    ...(billing.zipCode      && { zip:       billing.zipCode }),
    ...(billing.street       && { address:   billing.street }),
    ...(billing.addressLine2 && { address2:  billing.addressLine2 }),
    ...(formData?.phone      && { phone:     formData.phone }),
    ...(formData?.email      && { email:     formData.email }),
  };

  const resolvedAmount = resolveNuveiAmount({
    amount,
    itemDetails,
    platformItemDetails,
    selectedAddons,
  });

  const body = {
    // Only send `amount` when we have a positive value — a 0.00 amount
    // makes Nuvei return errCode 1201 at createPayment time. If we have
    // nothing, let the backend resolve the amount from the session.
    // Add-payment-source sessions ignore this entirely: the backend forces a
    // zero-authorization from the session type.
    ...(resolvedAmount != null && { amount: resolvedAmount }),
    currency: itemDetails?.currencyCode || "USD",
    ...(Object.keys(customer).length        && { customer }),
    ...(Object.keys(billingAddress).length  && { billingAddress }),
  };

  const { data } = await api.post(
    `/api/checkout/v1/${sessionId}/nuvei/open-order`,
    body
  );

  // { sessionToken, merchantId, merchantSiteId, env, zeroAuth, transactionType?, amount? }
  // The server decides whether this order is a zero-authorization; normalise the flag here
  // so callers never have to deal with the string form.
  return {
    ...data,
    zeroAuth: data?.zeroAuth === true || data?.zeroAuth === "true",
  };
};

export const fetchAdyenPaymentMethods = async ({
  sessionId,
  currency,
  country,
}) => {
  const { data } = await api.post(
    `/api/checkout/v1/${sessionId}/adyen/payment-methods`,
    {
      currency,
      billing: {
        address: {
          country,
        },
      },
    }
  );

  return data;
};


export default api;
